import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, CARD_DESCRIPTOR, siteImages } from '../data/mock';
import { Shield, PawPrint } from 'lucide-react';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-72 overflow-hidden">
          <img 
            src={siteImages.privacyBanner}
            alt="Privacy Policy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <Shield className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Your Privacy Matters</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                Privacy Policy
              </h1>
              <p className="text-brand-cream max-w-lg">
                How we collect, use, and protect your information
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-brand-cream border border-brand-divider rounded-3xl p-8 md:p-12">
            <div className="prose max-w-none">
              <p className="text-brand-brown-light mb-8">
                At Paw Supplies Group, operated by {CONTACT_INFO.corpName}, we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose and safeguard your information when you visit our website or make a purchase.
              </p>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>Information We Collect</h2>
              <p className="text-brand-brown-light mb-4">
                We collect information you provide directly to us, such as when you:
              </p>
              <ul className="text-brand-brown-light space-y-2 mb-6 list-disc list-inside">
                <li>Create an account or make a purchase</li>
                <li>Subscribe to our newsletter</li>
                <li>Contact us for support</li>
                <li>Fill out any forms on our website</li>
              </ul>
              <p className="text-brand-brown-light mb-4">
                This information may include your name, email address, shipping address, phone number, and payment information.
              </p>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>How We Use Your Information</h2>
              <p className="text-brand-brown-light mb-4">We use the information we collect to:</p>
              <ul className="text-brand-brown-light space-y-2 mb-6 list-disc list-inside">
                <li>Process and fulfill your orders</li>
                <li>Send you order confirmations and shipping updates</li>
                <li>Respond to your comments and questions</li>
                <li>Send promotional communications (with your consent)</li>
                <li>Improve our website and customer experience</li>
                <li>Prevent fraud and enhance security</li>
              </ul>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>Information Sharing</h2>
              <p className="text-brand-brown-light mb-4">
                We do not sell, trade, or otherwise transfer your personal information to outside parties except:
              </p>
              <ul className="text-brand-brown-light space-y-2 mb-6 list-disc list-inside">
                <li>To trusted third parties who assist us in operating our website and conducting our business</li>
                <li>When required by law or to protect our rights</li>
                <li>With your consent</li>
              </ul>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>Payment Security</h2>
              <p className="text-brand-brown-light mb-4">
                All transactions are processed through secure payment gateways. Your payment information is encrypted and never stored on our servers. Charges will appear as &quot;<span className="text-brand-orange-dark font-semibold">{CARD_DESCRIPTOR}</span>&quot; on your credit card statement.
              </p>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>Cookies</h2>
              <p className="text-brand-brown-light mb-4">
                We use cookies to enhance your experience on our website. Cookies help us understand how you interact with our site and allow us to improve our services.
              </p>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>Your Rights</h2>
              <p className="text-brand-brown-light mb-4">You have the right to:</p>
              <ul className="text-brand-brown-light space-y-2 mb-6 list-disc list-inside">
                <li>Access the personal information we have about you</li>
                <li>Request correction of your personal information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
              </ul>

              <h2 className="text-xl font-bold text-brand-brown mt-8 mb-4" className-FONT-DISPLAY>Contact Us</h2>
              <p className="text-brand-brown-light mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="bg-white border border-brand-divider rounded-xl p-6">
                <p className="text-brand-brown font-semibold">{CONTACT_INFO.corpName}</p>
                <p className="text-brand-brown-light">{CONTACT_INFO.address.street}</p>
                <p className="text-brand-brown-light">{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</p>
                <p className="text-brand-brown-light mt-2">Email: <a href={`mailto:${CONTACT_INFO.email}`} className="text-brand-orange-dark hover:underline">{CONTACT_INFO.email}</a></p>
                <p className="text-brand-brown-light">Phone: <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-brand-orange-dark hover:underline">{CONTACT_INFO.phone}</a></p>
              </div>

              <p className="text-brand-brown-light text-sm mt-8">
                Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
